"use client";

import { FC, useContext, useEffect, useRef, useState } from "react";

import { LinkHandler } from "@/components/common/LinkHandler";
import Logo from "@/components/common/Logo";
import { ArrowLeftIcon } from "@/components/icons/ArrowLeftIcon";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import UserContext, { UserContextType } from "@/context/UserContext";
import { GA4 } from "@/utils/ga4Utils";
import { cn, isProductPath } from "@/utils/general";
import { ThemeTypes } from "@/utils/theming";

import HeaderIcons from "./HeaderIcons";
import styles from "./HeaderSimple.module.scss";

// --- Type Definitions ---

interface HeaderSimpleProps {
  pathname: string;
  theme: ThemeTypes;
}

// --- Constants ---
const SHOP = "Shop";
const CONTINUE = "Continue Shopping";
const QUIZ = "Take the Quiz";

const HeaderSimple: FC<HeaderSimpleProps> = ({ pathname, theme = "master" }) => {
  const isMouseInHeader = useRef<boolean>(false);
  const userState = useContext<UserContextType>(UserContext); // Type the context
  const [yVal, setYVal] = useState<number>(0);

  // --- Derived State/Variables ---
  let showCart: boolean = true;
  let showQuiz: boolean = false;
  let isProductPage: boolean = false;
  let isAccountPage: boolean = false;
  let isIconVisibleOnRightSideOnly: boolean = false;
  let hideLeftSideContent: boolean = false;

  if (pathname) {
    showCart = pathname.indexOf("/checkout") === -1;
    isProductPage = isProductPath(pathname);
    isAccountPage = pathname.indexOf("account") > -1;
    hideLeftSideContent = pathname.indexOf("/promo/") > -1;
    showQuiz =
      pathname.indexOf("/why-nutrafol/") > -1 ||
      pathname.indexOf("/hair-growth-you-can-trust/") > -1;
    isIconVisibleOnRightSideOnly =
      showQuiz ||
      pathname.indexOf("/customer/account/login/") > -1 ||
      pathname.indexOf("/customer/account/create/") > -1;
  }

  const copy: string = isAccountPage && !isIconVisibleOnRightSideOnly ? SHOP : CONTINUE;

  // --- Effects ---
  useEffect(() => {
    const bodyElem = document.querySelectorAll("body")[0];

    const handleScroll = () => {
      if (!isMouseInHeader.current) {
        if (document.documentElement.scrollTop <= 85) {
          if (bodyElem.classList.contains("pagescrolled")) {
            bodyElem.classList.remove("pagescrolled");
          }
        } else if (yVal > document.documentElement.scrollTop) {
          if (bodyElem.classList.contains("pagescrolled")) {
            bodyElem.classList.remove("pagescrolled");
          }
        } else {
          if (!bodyElem.classList.contains("pagescrolled")) {
            bodyElem.classList.add("pagescrolled");
          }
        }
        setYVal(document.documentElement.scrollTop);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [yVal]); // setYVal is a stable function reference, no need to include

  // --- Dynamic Link Href ---
  let href: string =
    userState?.site === "men" ? "/hair/men/product/category/" : "/hair/brand/product/category/";

  if (showQuiz) {
    href = "/hair/brand/quiz/";
  }

  // --- Render ---
  return (
    <header
      data-mousedown // Custom data attribute
      className={cn(styles["header-wrap"], "header-simple")}
      onMouseEnter={() => (isMouseInHeader.current = true)}
      onMouseLeave={() => (isMouseInHeader.current = false)}
      onTouchEnd={() =>
        setTimeout(() => {
          isMouseInHeader.current = false;
        })
      }
    >
      <div className={styles["header-container"]}>
        <div className={"contents-container"}>
          <div className={cn(styles["simple-flex"])}>
            <div className={`left-wrap w-3/10 self-center flex-1`}>
              <LinkHandler
                path={href}
                onClick={() =>
                  GA4.onMarketingCtaClicked({
                    destination: href,
                    text: showQuiz ? QUIZ : SHOP,
                    type: "link",
                  })
                }
                className={styles["reverse-arrow-link"]}
              >
                {showQuiz ? (
                  <TextElementNova element="span" className={"CTA-10-Dia-Normal"} text={QUIZ} />
                ) : (
                  <>
                    {/* Mobile View */}
                    {hideLeftSideContent ? null : (
                      <TextElementNova
                        element="span"
                        className={"CTA-10-Dia-Normal flex md:hidden"}
                      >
                        <div className={styles["arrow-flex"]}>
                          <div className={``}>
                            <ArrowLeftIcon />
                          </div>
                          <div>{SHOP}</div>
                        </div>
                      </TextElementNova>
                    )}
                    {/* Desktop View */}
                    {hideLeftSideContent ? null : (
                      <TextElementNova
                        element="span"
                        className={"CTA-10-Dia-Normal hidden md:flex"}
                      >
                        <div className={styles["arrow-flex"]}>
                          {isIconVisibleOnRightSideOnly || copy === CONTINUE ? null : (
                            <div>
                              <ArrowLeftIcon />
                            </div>
                          )}
                          <div>{copy}</div>
                        </div>
                      </TextElementNova>
                    )}
                  </>
                )}
              </LinkHandler>
            </div>
            <div className={"w-4/10 self-center center-wrap max-w-[141px] lg:max-w-[156px]"}>
              <Logo isSimple={true} theme={theme} />
            </div>
            <div className={"w-3/10 self-center right-wrap flex-1"}>
              <div className={`flex flex-wrap items-center justify-end mt-[1px]`}>
                <HeaderIcons
                  theme={theme}
                  isSimple={true}
                  isIconVisibleOnRightSideOnly={isIconVisibleOnRightSideOnly}
                  isProductPage={isProductPage}
                  isAccountPage={isAccountPage}
                  showCart={showCart}
                  pathname={pathname}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeaderSimple;
